import { supabase } from '../../core/config/supabase';
import { ChallengeEntity, ChallengeWithUserEntity } from '../entities';
import { logger } from '@/src/core/utils';

export class ChallengeDataSource {
  async create(
    challengerId: string,
    challengedId: string,
    message?: string
  ): Promise<ChallengeEntity> {
    const { data, error } = await supabase
      .from('challenges')
      .insert({
        challenger_id: challengerId,
        challenged_id: challengedId,
        message,
        status: 'pending',
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as ChallengeEntity;
  }

  async update(
    challengeId: string,
    status: 'accepted' | 'declined' | 'cancelled',
    tournamentId?: string
  ): Promise<ChallengeEntity> {
    const updateData: any = { status, updated_at: new Date().toISOString() };
    if (tournamentId) {
      updateData.tournament_id = tournamentId;
    }

    const { data, error } = await supabase
      .from('challenges')
      .update(updateData)
      .eq('id', challengeId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from update');

    return data as ChallengeEntity;
  }

  async getChallengesReceived(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ChallengeWithUserEntity[]> {
    logger.debug('ChallengeDataSource', 'Calling RPC', { functionName: 'get_challenges_received' });

    const { data, error } = await supabase.rpc('get_challenges_received', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('ChallengeDataSource', 'getChallengesReceived failed', { error });
      throw new Error(error.message);
    }

    logger.debug('ChallengeDataSource', 'Mapped items', { count: data?.length ?? 0 });

    return (data as ChallengeWithUserEntity[]) || [];
  }

  async getChallengesSent(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ChallengeWithUserEntity[]> {
    logger.debug('ChallengeDataSource', 'Calling RPC', { functionName: 'get_challenges_sent' });

    const { data, error } = await supabase.rpc('get_challenges_sent', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('ChallengeDataSource', 'getChallengesSent failed', { error });
      throw new Error(error.message);
    }

    logger.debug('ChallengeDataSource', 'Mapped items', { count: data?.length ?? 0 });

    return (data as ChallengeWithUserEntity[]) || [];
  }
}
